import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Send, MessageCircle, BookOpen, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// localStorage key owned by this component. The onboarding callout shows
// exactly once per browser+agent. The pulse animation used to be gated
// similarly but is now a purely visual, one-shot cue on every fresh page
// load — see the pulse effect below.
const CALLOUT_SEEN_KEY = "ikey_support_callout_seen_v1";

/**
 * SupportBubble — agent-side floating iKEY-branded FAB.
 *
 * Behaviors added for feedback visibility:
 *   1. Listens for a global `ikey:open-support` event so the profile
 *      dropdown ("App Feedback & Support") can open the same panel.
 *   2. Shows a hover tooltip ("App Feedback & Support") on the FAB.
 *   3. On the agent's first landing (any page after login), pops a
 *      chat-style callout beside the bubble for 5 seconds — once.
 *   4. Runs a one-shot pulse ring the first time the agent lands on
 *      the dashboard route (`/`).
 */
export default function SupportBubble() {
  const { agent } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [category, setCategory] = useState("general");
  const [body, setBody] = useState("");
  const [showCallout, setShowCallout] = useState(false);
  const [pulse, setPulse] = useState(false);
  // Refs so effects can dismiss timers if the popover opens mid-callout.
  const calloutTimer = useRef(null);
  const pulseTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Reset the form when the popover closes so re-opening feels fresh.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { setSent(false); setBody(""); setCategory("general"); }, 250);
      return () => clearTimeout(t);
    }
    // Opening the popover dismisses the callout so it doesn't collide.
    setShowCallout(false);
    if (calloutTimer.current) clearTimeout(calloutTimer.current);
  }, [open]);

  // First-login callout — 5-second auto-dismissing chat bubble beside the FAB.
  useEffect(() => {
    if (!agent) return;
    try {
      if (localStorage.getItem(CALLOUT_SEEN_KEY)) return;
    } catch (e) { console.debug("localStorage read failed (private mode?)", e); }
    // Wait a beat so it doesn't collide with the welcome tour.
    const showT = setTimeout(() => {
      setShowCallout(true);
      calloutTimer.current = setTimeout(() => setShowCallout(false), 5000);
      try {
        localStorage.setItem(CALLOUT_SEEN_KEY, "1");
      } catch (e) { console.debug("localStorage write failed (private mode?)", e); }
    }, 1500);
    return () => {
      clearTimeout(showT);
      if (calloutTimer.current) clearTimeout(calloutTimer.current);
    };
  }, [agent]);

  // One-shot pulse on fresh page load. Runs a single iteration of the ping
  // animation so the eye lands on the bubble once, then goes fully static —
  // no continuous pulsing. Fires on every hard page load / refresh but not
  // on SPA navigation (the component stays mounted across route changes).
  const pulsedThisMount = useRef(false);
  useEffect(() => {
    if (!agent || pulsedThisMount.current) return;
    pulsedThisMount.current = true;
    setPulse(true);
    // 1.4s = one ping cycle. Match `animation-duration` on the ring below.
    pulseTimer.current = setTimeout(() => setPulse(false), 1400);
    return () => { if (pulseTimer.current) clearTimeout(pulseTimer.current); };
  }, [agent]);

  // External open event — the "App Feedback & Support" menu item fires this.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("ikey:open-support", onOpen);
    return () => window.removeEventListener("ikey:open-support", onOpen);
  }, []);

  if (!agent) return null;

  const submit = async () => {
    const trimmed = body.trim();
    if (trimmed.length < 3) { toast.error("Please share a bit more"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/support/feedback", {
        body: trimmed,
        category,
        page_path: location.pathname,
      });
      setSent(true);
      // Fall back to a generic thanks if the server ever forgets to send one.
      toast.success(data?.message || "Thanks — we received your feedback");
    } catch (e) {
      // 422 responses come back with `detail` as an ARRAY of validation
      // errors. Passing an array to sonner crashes React rendering, so
      // stringify anything that isn't already a string.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d?.msg || JSON.stringify(d)).join(", ")
          : "Could not send. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[70] w-[340px] rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
          role="dialog"
          aria-label="iKEY support"
          data-testid="support-popover"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <img src="/ikey-logo.png" alt="" className="h-5 w-5 rounded" />
              <span className="font-semibold text-slate-900 text-sm">iKEY Support</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Close support"
              data-testid="support-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {sent ? (
            <div className="p-6 text-center" data-testid="support-thanks">
              <div
                className="mx-auto h-12 w-12 rounded-full flex items-center justify-center"
                style={{ background: "var(--primary-hex)" }}
              >
                <img src="/ikey-logo.png" alt="" className="h-7 w-7 rounded" />
              </div>
              <h4 className="mt-3 font-heading text-lg font-bold text-slate-900">Thank you</h4>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                We've logged your feedback and will reach out if we have further questions.
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-semibold text-white"
                style={{ background: "var(--primary-hex)" }}
                data-testid="support-thanks-close"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <p className="text-xs text-slate-500 leading-relaxed">
                Share a bug, an idea, or just a note. We read every message.
              </p>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Type</label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
                  data-testid="support-category"
                >
                  <option value="general">General feedback</option>
                  <option value="bug">Bug report</option>
                  <option value="feature">Feature request</option>
                  <option value="billing">Billing question</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Message</label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={4}
                  placeholder="Tell us what's on your mind…"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm resize-none"
                  data-testid="support-body"
                />
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={busy || body.trim().length < 3}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "var(--primary-hex)" }}
                data-testid="support-submit"
              >
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send feedback"}
              </button>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-around text-[11px] text-slate-500">
                <a href="mailto:support@ikeycrm.com" className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="support-email">
                  <Mail className="h-3 w-3" /> Email
                </a>
                <a href="/community" className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="support-community">
                  <MessageCircle className="h-3 w-3" /> Community
                </a>
                <a href="/terms" className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="support-docs">
                  <BookOpen className="h-3 w-3" /> Terms
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* First-login chat callout — 5-second auto-dismiss, dismissable
          by clicking the ✕ or opening the popover. */}
      {showCallout && !open && (
        <div
          className="fixed bottom-24 right-6 z-[70] w-64 rounded-xl bg-slate-900 text-white shadow-xl p-3 pr-8 animate-in fade-in slide-in-from-bottom-2"
          role="status"
          data-testid="support-callout"
        >
          <button
            type="button"
            onClick={() => setShowCallout(false)}
            className="absolute top-2 right-2 text-white/60 hover:text-white p-0.5"
            aria-label="Dismiss"
            data-testid="support-callout-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <p className="text-xs leading-relaxed">
            <span className="font-semibold">Have a suggestion or found a bug?</span> Let us know here!
          </p>
          {/* Little tail pointing at the FAB below. */}
          <span
            className="absolute -bottom-1.5 right-8 h-3 w-3 rotate-45 bg-slate-900"
            aria-hidden="true"
          />
        </div>
      )}

      <div className="fixed bottom-6 right-6 z-[70] group">
        {/* Hover tooltip — visible on group-hover / focus-within. */}
        <span
          className="pointer-events-none absolute right-16 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 text-white text-xs font-semibold px-2.5 py-1.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity shadow-lg"
          data-testid="support-bubble-tooltip"
        >
          App Feedback & Support
        </span>
        {/* One-shot pulse — a single iteration of the ping animation on fresh
            page load to subtly draw the eye. No continuous pulsing; the bubble
            is otherwise fully static. Sits behind the button so it doesn't
            intercept clicks. */}
        {pulse && !open && (
          <span
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: "var(--primary-hex, #1F6FEB)",
              opacity: 0.4,
              // Tailwind's `animate-ping` is defined with
              // `animation-iteration-count: infinite`. We reuse the same
              // keyframe (`ping`) but force a single iteration so the pulse
              // fires exactly once and then stops.
              animation: "ping 1.4s cubic-bezier(0, 0, 0.2, 1) 1",
            }}
            aria-hidden="true"
            data-testid="support-bubble-pulse"
          />
        )}
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="relative h-14 w-14 rounded-full shadow-xl bg-white flex items-center justify-center hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-slate-900/20 ring-1 ring-slate-200"
          aria-label={open ? "Close support" : "App Feedback & Support"}
          aria-expanded={open}
          title="App Feedback & Support"
          data-testid="support-bubble"
        >
          {open ? (
            <X className="h-5 w-5 text-slate-700" />
          ) : (
            <img src="/ikey-logo.png" alt="iKEY support" className="h-8 w-8 rounded-lg" />
          )}
        </button>
      </div>
    </>
  );
}
